import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, FunnelSimple, Plus, FileCsv } from "@phosphor-icons/react";
import { api, formatApiError, API_BASE } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { STATUS_META, formatMoney, formatDateTime } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const STATUS_OPTIONS = Object.keys(STATUS_META);

export default function Orders() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const status = params.get("status") || "";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders", {
        params: { status: status || undefined, search: search || undefined },
      });
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const setStatus = (s) => {
    if (!s) {
      params.delete("status");
    } else {
      params.set("status", s);
    }
    setParams(params, { replace: true });
  };

  const onSearch = (e) => {
    e.preventDefault();
    fetchOrders();
  };

  const canCreate = user.role === "sales" || user.role === "super_admin";

  const exportCsv = async () => {
    try {
      const res = await api.get("/orders/export.csv", {
        params: { status: status || undefined },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "solar-africa-orders.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="orders-page">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2">// Orders</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Order Pipeline</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="btn-ghost inline-flex items-center gap-2" data-testid="export-csv-btn">
            <FileCsv size={16} weight="bold" /> Export CSV
          </button>
          {canCreate && (
            <Link to="/orders/new" className="btn-solar inline-flex items-center gap-2" data-testid="new-order-btn">
              <Plus size={16} weight="bold" /> New Order
            </Link>
          )}
        </div>
      </header>

      <div className="grid-line bg-white p-4 mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={onSearch} className="flex items-center gap-2 flex-1 min-w-[260px]">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              className="input-tech pl-9"
              placeholder="Search by Order ID, customer name or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
          <button type="submit" className="btn-ghost" data-testid="search-btn">Search</button>
        </form>
        <div className="flex items-center gap-2">
          <FunnelSimple size={16} />
          <select
            className="select-tech"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="status-filter"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid-line bg-white overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center overline">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center">
            <div className="overline mb-2">No orders found</div>
            <div className="text-sm text-[var(--muted)]">Try clearing filters or creating a new order.</div>
          </div>
        ) : (
          <table className="data-table" data-testid="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Sales</th>
                <th>Admin</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.order_id}`}>
                  <td className="font-mono-tech text-xs">{o.order_id}</td>
                  <td className="font-medium">{o.customer.name}<div className="text-xs text-[var(--muted)]">{o.customer.phone}</div></td>
                  <td className="text-xs">{o.items.length} item{o.items.length === 1 ? "" : "s"}</td>
                  <td className="font-medium">{formatMoney(o.total_amount)}</td>
                  <td className="text-xs">{o.sales_rep_name}</td>
                  <td className="text-xs">{o.assigned_admin_name}</td>
                  <td><StatusBadge status={o.status} /></td>
                  <td className="text-xs text-[var(--muted)]">{formatDateTime(o.created_at)}</td>
                  <td>
                    <Link
                      to={`/orders/${o.id}`}
                      className="text-xs font-bold uppercase tracking-widest text-[var(--solar)] hover:underline"
                      data-testid={`open-${o.order_id}`}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
